import { IProjectData, IPermissionData } from '@droid/project-interface'
import { IRhinoRunnerConf, IAssembleRhinoRunnerOptions } from './IRhinoRunnerConf'
import { assembleProjectScript } from './assembleProjectScript'
import { ulid } from 'ulid'

/**
 * 组装完整的 RhinoRunner 配置对象
 *
 * 此函数将 IProjectData 转换为完整的 RhinoRunner 配置,包括:
 * - 组装可执行脚本
 * - 设置权限列表
 * - 配置前台服务
 * - 设置其他运行时选项
 *
 * @param projectData - Project 数据对象
 * @param permissions - 执行所需的权限列表
 * @param options - 可选的配置选项
 * @returns 完整的 RhinoRunner 配置对象
 *
 * @example
 * const permissions = getProjectPermissionsData(projectData)
 * const conf = assembleRhinoRunner(projectData, permissions, {
 *     foregroundText: '正在执行自动化任务...',
 *     onlyRunSingle: true
 * })
 * await autoWebViewJs.runProject(conf)
 */
export function assembleRhinoRunner(
    projectData: IProjectData,
    permissions: IPermissionData[],
    options?: IAssembleRhinoRunnerOptions
): IRhinoRunnerConf {
    // 组装脚本
    const script = assembleProjectScript(projectData)

    // 构建配置对象
    const conf: IRhinoRunnerConf = {
        // 基础配置
        bridgeId: options?.bridgeId ?? ulid(),
        scriptId: options?.scriptId ?? projectData.client_id ?? ulid(),
        script,

        // 运行时配置
        onlyRunSingle: options?.onlyRunSingle ?? true,
        stopPrevIfOnlyRunSingle: options?.stopPrevIfOnlyRunSingle ?? true,
        forceStopTime: options?.forceStopTime ?? 0,

        // 前台服务配置
        foregroundText: options?.foregroundText ?? `正在执行: ${projectData.name}`,

        // 权限配置
        permissions,

        // 其他配置
        injects: options?.injects,
        params: options?.params,
        languageVersion: options?.languageVersion ?? 200, // ES5+
        optimizationLevel: options?.optimizationLevel ?? -1, // 解释模式
        printScript: options?.printScript ?? false
    }

    return conf
}
